import React from 'react';
import { warmGrey, divisionColors, colors, breakpoints, fontWeight } from '@mergermarket/acuris-react-styles';
import styled, { css } from 'styled-components';

const DEFAULT_SIZE = 'large';
const tickStyles = {
  ['small']: css`
    height: 0.8rem;
    left: -0.8rem;
    top: 0.4rem;
    width: 0.4rem;
  `,
  ['medium']: css`
    height: 1rem;
    left: -1rem;
    top: 0.4rem;
    width: 0.5rem;
  `,
  ['large']: css`
    height: 1.4rem;
    left: -1.4rem;
    top: 0.3rem;
    width: 0.6rem;
  `
};
const getTickStyle = size => {
  return tickStyles[size || DEFAULT_SIZE];
};
const indeterminateStyles = {
  ['small']: css`
    height: 0.6rem;
    left: -0.7rem;
    top: 0.6rem;
    width: 0.2rem;
  `,
  ['medium']: css`
    height: 0.8rem;
    left: -1.1rem;
    top: 0.4rem;
    width: 0.6rem;
  `,
  ['large']: css`
    height: 1rem;
    left: -1.5rem;
    top: 0.4rem;
    width: 0.8rem;
  `
};
const getIndeterminateStyle = size => {
  return indeterminateStyles[size || DEFAULT_SIZE];
};
const boxStyles = {
  ['small']: css`
    height: 1.2rem;
    width: 1.2rem;
    left: -1.2rem;
    top: 0.3rem;
  `,
  ['medium']: css`
    width: 1.6rem;
    height: 1.6rem;
    left: -1.6rem;
    top: 0.2rem;
  `,
  ['large']: css`
    width: 2.2rem;
    height: 2.2rem;
    left: -2.2rem;
    top: 0.1rem;
  `
};
const getBoxStyle = size => {
  return boxStyles[size || DEFAULT_SIZE];
};
const labelSizes = {
  ['small']: css`
    padding-left: 0.6rem;
    line-height: 1.5;
    font-size: 1.2rem;
    min-height: 1.6rem;
  `,
  ['medium']: css`
    padding-left: 0.6rem;
    line-height: 1.69;
    font-size: 1.2rem;
    min-height: 2rem;
  `,
  ['large']: css`
    padding-left: 0.8rem;
    line-height: 1.69;
    font-size: 1.4rem;
    min-height: 2.4rem;
  `
};
const getLabelStyle = size => {
  return labelSizes[size || DEFAULT_SIZE];
};
const sizeProperties = {
  ['small']: {
    border: {
      width: '0.1rem'
    },
    padding: '1.2rem'
  },
  ['medium']: {
    border: {
      width: '0.2rem'
    },
    padding: '1.6rem'
  },
  ['large']: {
    border: {
      width: '0.2rem'
    },
    padding: '2.2rem'
  }
};
const getSizeProperties = size => {
  return sizeProperties[size || 'large'];
};

/* README: Implementation notes
 *
 * In this component, the default browser input element is hidden
 * Visible instead are pseudo elements on the label element, which is a sibling of the input
 * The :before element is used for the box
 * the :after element is used for the check, or indeterminate mark
 *
 * Some other components, e.g. the universal card, need to be able to style the checkbox as "checked" when the card is hovered
 * To do this we expose a method `styleCheckbox` which allows specific attributes to be set,
 * without knowledge of the checkbox internals
 * styleCheckbox must be used within the top level of another styled component
 * OR within a css`` block, or interpolation will break and styles will apply to parent component
 */
const accessiblyHidden = css`
  border: 0 !important;
  clip: rect(0 0 0 0) !important;
  height: 1px !important;
  margin: -1px !important;
  overflow: hidden !important;
  padding: 0 !important;
  position: absolute !important;
  width: 1px !important;
`;
const visiblePseudoElement = css`
  content: '';
  display: block;
  position: absolute;
`;

const accessiblyHideable = ({
  accessiblyHide
}) => accessiblyHide ? accessiblyHidden : '';

const colorMap = {
  border: {
    default: warmGrey.V,
    focus: warmGrey.VII,
    active: warmGrey.VII,
    hover: warmGrey.VIII,
    disabled: warmGrey.VII,
    checked: divisionColors.darker
  },
  checkbox: {
    stroke: {
      disabled: warmGrey.I,
      checked: colors.primaryBackground,
      smallHover: warmGrey.VI,
      active: divisionColors.darker,
      focus: divisionColors.darker
    },
    fill: {
      default: colors.primaryBackground,
      disabledUnchecked: warmGrey.I,
      disabledChecked: warmGrey.VII,
      checked: divisionColors.darker
    }
  },
  text: {
    default: colors.primaryFont,
    disabled: colors.disabled
  }
};
const Input = styled.input.attrs({
  type: 'checkbox'
})`
  :focus {
    outline: none;
  }
  position: absolute;
  height: 1px;
  width: 1px;
  transform: scale(0.1);
  opacity: 0;
  filter: alpha(opacity=0);

  left: 0.6rem;
  top: 1rem;
`;
const Wrapper = styled.div`
  display: inline-block;
  position: relative;
  padding: ${({
  size
}) => `0 0 0 ${getSizeProperties(size).padding}`};

  @media screen and (min-width: ${breakpoints.xsmall}) {
    &:hover {
      /* prettier-ignore */
      ${Input}:not(:disabled):not(:checked):not(:indeterminate) ~ label:after {
        border-color: ${colorMap.checkbox.stroke.smallHover};
        border-style: solid;
        border-width: ${({
  size
}) => {
  const borderWidth = getSizeProperties(size).border.width;
  return `0 ${borderWidth} ${borderWidth} 0;`;
}}
        ${({
  size
}) => getTickStyle(size)}
      }
      /* prettier-ignore */
      ${Input}:not(:disabled):not(:checked):not(:indeterminate) ~ label:before {
        border-color: ${colorMap.border.hover};
      }
    }
  }
`;
const LabelInner = styled.span`
  ${accessiblyHideable}

  ${props => props.color && props.color === 'inverse' && `
      color:${colors.primaryFontInverse};
    `}
`;
const Label = styled.label`
  display: block;
  position: relative;
  cursor: pointer;
  ${({
  size
}) => getLabelStyle(size)}

  ${Input}:disabled ~ & {
    cursor: auto;

    ${LabelInner} {
      color: ${colors.disabled};
    }
  }

  ${Input}:checked ~ &,
  ${Input}:indeterminate ~ & {
    font-weight: ${fontWeight.bold};
  }

  &:after {
    ${visiblePseudoElement}
  }

  ${Input}:checked ~ &:after {
    ${({
  size
}) => getTickStyle(size)}
  }

  ${Input}:indeterminate ~ &:after {
    ${({
  size
}) => getIndeterminateStyle(size)}
  }

  ${Input}:hover ~ &:after {
    border-color: transparent;
  }

  // the tick when input is checked
  ${Input}:checked ~ &:after,
  ${Input}:indeterminate ~ &:after {
    border-color: ${colorMap.checkbox.stroke.checked};
  }

  ${Input}:checked ~ &:after,
  ${Input}:indeterminate ~ &:after {
    border-color: ${colorMap.checkbox.stroke.checked};
  }

  // the tick when input is disabled
  ${Input}:disabled ~ &:after {
    border-color: ${colorMap.checkbox.stroke.disabled};
  }

  &:before {
    ${visiblePseudoElement}
    border: 0.1rem solid;
    background: ${colorMap.checkbox.fill.default};
    border-color: ${colorMap.border.default};
    ${({
  size
}) => getBoxStyle(size)}
  }

  & ~ ${Input} {
    ${({
  size
}) => getLabelStyle(size)}
  }

  ${Input}:checked ~ &:before,
  ${Input}:indeterminate ~ &:before {
    border-color: ${colorMap.border.checked};
    background: ${colorMap.checkbox.fill.checked};
  }

  ${Input}:disabled ~ &:before {
    border-color: ${colorMap.border.disabled};
    background: ${colorMap.checkbox.fill.disabledUnchecked};
  }

  // the tick when input input is checked
  ${Input}:checked ~ &:after {
    border-style: solid;
    border-width: ${({
  size
}) => `0 ${getSizeProperties(size).border.width} ${getSizeProperties(size).border.width} 0`};
  }

  // the dash when input is indeterminate
  ${Input}:indeterminate ~ &:after {
    border-style: solid;
    border-width: ${({
  size
}) => `0 ${getSizeProperties(size).border.width} 0 0`};
  }

  ${Input}:checked ~ &:after,
  ${Input}:indeterminate ~ &:after,
  ${Wrapper}:hover &:after {
    ${visiblePseudoElement}
  }

  ${Input}:checked ~ &:after,
  ${Wrapper}:hover &:after {
    transform: rotate(45deg);
  }

  ${Input}:indeterminate ~ &:after {
    transform: rotate(90deg);
  }

  ${Input}:disabled:checked ~ &:before,
  ${Input}:disabled:indeterminate ~ &:before {
    border-color: ${colorMap.border.disabled};
    background: ${colorMap.checkbox.fill.disabledChecked};
  }

  // box background when input is focused and checked/indeterminate
  ${Input}:checked:focus ~ &:before,
  ${Input}:indeterminate:focus ~ &:before {
    background-color: ${colors.primaryBackground};
    border-color: ${colors.primaryBrandHover};
  }

  // check color is focused and checked/indeterminate
  ${Input}:checked:focus ~ &:after,
  ${Input}:indeterminate:focus ~ &:after {
    border-color: ${colors.primaryBrandHover};
  }


  ${Input}:focus ~ &:before {
    ${props => props.color === 'inverse' ? css`
              border-color: ${colorMap.border.focus};
            ` : css`
              border-color: ${colorMap.border.focus};
            ` // TODO should the border color vary by inverse color state?
}
  }

  ${Input}:hover ~ &:before {
    ${props => props.color === 'inverse' && `
        border-color: ${colorMap.border.checked};
        `}
  }

  ${Input}:active ~ &:before {
    ${props => props.color === 'inverse' ? `
        border-color: ${colorMap.border.checked};
        ` : `
        border-color: ${colorMap.border.active};
        `}
  }
`;
const styleCheckbox = ({
  showCheck,
  size = 'small',
  backgroundColor,
  checkColor,
  checkboxBorderColor
}) => css`
  ${showCheck && css`
      ${Label}:after {
        ${visiblePseudoElement}
        ${getTickStyle(size)}
        border-style: solid;
        transform: rotate(45deg);
        ${css`
          border-width: 0 ${getSizeProperties(size).border.width} ${getSizeProperties(size).border.width} 0;
        `}
      }
    `}

  ${checkColor && css`
      ${Label}:after {
        border-color: ${checkColor};
      }
    `}

  ${checkboxBorderColor && css`
      ${Label}:before {
        border-color: ${checkboxBorderColor};
      }
    `}

  ${backgroundColor && css`
      ${Label}:before {
        background: ${backgroundColor};
      }
    `}
`;
const CheckboxLabel = ({
  children,
  accessiblyHide,
  id,
  size,
  color
}) => /*#__PURE__*/React.createElement(Label, {
  size: size,
  htmlFor: id,
  "data-test-label": true,
  color: color
}, /*#__PURE__*/React.createElement(LabelInner, {
  accessiblyHide: accessiblyHide,
  color: color
}, children));

const Checkbox = ({
  id,
  label,
  checked,
  disabled,
  size,
  color,
  indeterminate = false,
  name,
  showText = false,
  autoComplete,
  className = '',
  onChange
}) => {
  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-test-checkbox-wrapper": true,
    className: className,
    size: size
  }, /*#__PURE__*/React.createElement(Input, {
    onChange: onChange,
    autoComplete: autoComplete,
    checked: checked,
    disabled: disabled,
    id: id,
    name: name,
    value: id,
    ref: input => {
      if (input) {
        input.indeterminate = indeterminate;
      }
    }
  }), /*#__PURE__*/React.createElement(CheckboxLabel, {
    size: size,
    id: id,
    accessiblyHide: !showText,
    color: color
  }, label));
};

const StyledCheckboxList = styled.ul`
  margin-left: 0;
  list-style: none;
  padding-left: 0;
`;
const StyledCheckboxListItem = styled.li`
  margin: 0.8rem 0;
  font-size: 1.2rem;
  line-height: 1;
`;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const CheckboxList = ({
  checkboxOptions,
  color,
  className,
  showText,
  size,
  onChange
}) => /*#__PURE__*/React.createElement(StyledCheckboxList, {
  className: className
}, checkboxOptions.map(item => /*#__PURE__*/React.createElement(StyledCheckboxListItem, {
  key: item.id
}, /*#__PURE__*/React.createElement(Checkbox, _extends({
  size: size,
  color: color,
  showText: showText,
  onChange: e => {
    onChange(item.id, e);
  }
}, item)))));

export { Checkbox, CheckboxList, getTickStyle, styleCheckbox };
//# sourceMappingURL=index.js.map
